// CB:BEGIN pm-channel-onboarding
/**
 * Property Manager Onboarding (/pm-onboarding)
 *
 * 4-step activation flow for new property manager accounts:
 *   Step 0 — Platform Agreement (sign before proceeding)
 *   Step 1 — Profile setup (company name, contact, portfolio size)
 *   Step 2 — Add first property
 *   Step 3 — Create first project (optional, with skip)
 *   (Step 4 is the success/done screen — not counted in TOTAL_STEPS)
 *
 * After Step 1, sets localStorage 'cb.usertype' = 'property_manager' so
 * App.tsx role gating allows navigation to pm-dashboard without requiring
 * Clerk metadata update (a known future enhancement).
 *
 * CTA after completion → pm-dashboard.
 */

import React, { useState, useCallback } from 'react';
import ContentLimiter from '../components/layout/ContentLimiter';
import { useDevice } from '../hooks/useDevice';
import { useAuthFetch } from '../lib/useAuthFetch';
import { getApiUrl } from '../lib/getApiUrl';
import { CheckCircle2, Building2, ArrowRight, ChevronLeft } from 'lucide-react';

const BRAND_GREEN = '#123c3b';
const TOTAL_STEPS = 4;
// Sync with CURRENT_PM_PLATFORM_AGREEMENT_VERSION in apps/api/src/config/compliance.ts
const PM_AGREEMENT_VERSION = '1';

const PROJECT_TYPES = [
  'ROOF',
  'SIDING',
  'GUTTERS',
  'WINDOWS',
  'DOORS',
  'PAINTING',
  'OTHER',
] as const;

const PROJECT_TYPE_LABELS: Record<string, string> = {
  ROOF: 'Roof Repair / Replacement',
  SIDING: 'Siding Repair / Replacement',
  GUTTERS: 'Gutter Repair / Replacement',
  WINDOWS: 'Windows',
  DOORS: 'Doors',
  PAINTING: 'Exterior Painting',
  OTHER: 'Other Exterior Work',
};

const PROPERTY_TYPES = [
  { value: 'RESIDENTIAL', label: 'Single Family / Residential' },
  { value: 'MULTIFAMILY', label: 'Multi-Family / Apartments' },
  { value: 'HOA', label: 'HOA / Community' },
  { value: 'COMMERCIAL', label: 'Commercial / Retail / Office' },
  { value: 'OTHER', label: 'Other' },
];

const PORTFOLIO_SIZES = [
  { value: '1', label: '1 property' },
  { value: '2-5', label: '2–5 properties' },
  { value: '6-20', label: '6–20 properties' },
  { value: '21-50', label: '21–50 properties' },
  { value: '50+', label: '50+ properties' },
];

interface PropertyManagerOnboardingProps {
  onNavigate: (screen: string) => void;
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '12px 14px',
  border: '1.5px solid #e2e8f0',
  borderRadius: 10,
  fontSize: 15,
  color: '#0f172a',
  background: '#fff',
  outline: 'none',
  boxSizing: 'border-box',
  transition: 'border-color 0.2s',
};

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: 13,
  fontWeight: 600,
  color: '#475569',
  marginBottom: 6,
};

const fieldWrap: React.CSSProperties = { marginBottom: 18 };

// CB:BEGIN pm-onboarding-stable-subcomponents
/** Defined at module scope so React does not treat them as new component types each render (avoids remounting inputs and focus jumping to autoFocus). */
const PMOnboardingCard: React.FC<{ isMobileLayout: boolean; children: React.ReactNode }> = ({ isMobileLayout, children }) => (
  <div
    style={{
      background: '#fff',
      borderRadius: 20,
      border: '1.5px solid #e2e8f0',
      padding: isMobileLayout ? '28px 20px' : '44px 48px',
      boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
      maxWidth: 580,
      width: '100%',
      margin: '0 auto',
    }}
  >
    {children}
  </div>
);

const PMOnboardingProgressBar: React.FC<{ step: number; brandGreen: string }> = ({ step, brandGreen }) => (
  <div style={{ marginBottom: 36 }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10, fontSize: 13, color: '#94a3b8', fontWeight: 500 }}>
      <span>Step {step + 1} of {TOTAL_STEPS}</span>
      <span style={{ color: brandGreen, fontWeight: 600 }}>
        {['Agreement', 'First Property', 'Company Profile', 'First Project', 'All Done'][step]}
      </span>
    </div>
    <div style={{ height: 6, background: '#e2e8f0', borderRadius: 99, overflow: 'hidden' }}>
      <div
        style={{
          height: '100%',
          width: `${((step + 1) / TOTAL_STEPS) * 100}%`,
          background: `linear-gradient(90deg, ${brandGreen}, #1a6b69)`,
          borderRadius: 99,
          transition: 'width 0.4s ease',
        }}
      />
    </div>
  </div>
);

const PMSectionTitle: React.FC<{ title: string; subtitle: string; isMobileLayout: boolean }> = ({ title, subtitle, isMobileLayout }) => (
  <div style={{ marginBottom: 28 }}>
    <h2 style={{ fontSize: isMobileLayout ? 22 : 26, fontWeight: 800, color: '#0f172a', marginBottom: 8, lineHeight: 1.2 }}>{title}</h2>
    <p style={{ fontSize: 15, color: '#64748b', lineHeight: 1.5 }}>{subtitle}</p>
  </div>
);

const PMErrorBanner: React.FC<{ error: string | null }> = ({ error }) =>
  error ? (
    <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10, padding: '12px 16px', marginBottom: 20, fontSize: 14, color: '#dc2626' }}>
      {error}
    </div>
  ) : null;

const PMPrimaryBtn: React.FC<{
  label: string;
  loading?: boolean;
  onClick?: () => void;
  type?: 'submit' | 'button';
  brandGreen: string;
}> = ({ label, loading, onClick, type = 'submit', brandGreen }) => (
  <button
    type={type}
    onClick={onClick}
    disabled={loading}
    style={{
      width: '100%',
      padding: '14px 24px',
      background: loading ? '#94a3b8' : brandGreen,
      color: '#fff',
      border: 'none',
      borderRadius: 12,
      fontSize: 16,
      fontWeight: 700,
      cursor: loading ? 'not-allowed' : 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 8,
      minHeight: 48,
      transition: 'background 0.2s',
    }}
  >
    {loading ? 'Saving…' : label}
    {!loading && <ArrowRight size={18} />}
  </button>
);

const PMBackBtn: React.FC<{ show: boolean; onBack: () => void }> = ({ show, onBack }) =>
  show ? (
    <button
      type="button"
      onClick={onBack}
      style={{ display: 'flex', alignItems: 'center', gap: 4, background: 'none', border: 'none', color: '#94a3b8', fontSize: 13, cursor: 'pointer', marginBottom: 8 }}
    >
      <ChevronLeft size={14} /> Back
    </button>
  ) : null;

const PMReassurance: React.FC<{ text: string }> = ({ text }) => (
  <p style={{ marginTop: 16, fontSize: 13, color: '#94a3b8', textAlign: 'center' }}>{text}</p>
);

// Module scope — same reason as other PM* wrappers (stable identity, no focus steal on re-render)
const PMSkipLink: React.FC<{ label: string; onClick: () => void }> = ({ label, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    style={{ display: 'block', width: '100%', marginTop: 12, padding: '10px', background: 'none', border: 'none', color: '#94a3b8', fontSize: 14, cursor: 'pointer', textAlign: 'center' }}
  >
    {label}
  </button>
);
// CB:END pm-onboarding-stable-subcomponents

const PropertyManagerOnboarding: React.FC<PropertyManagerOnboardingProps> = ({ onNavigate }) => {
  const { isMobileLayout } = useDevice();
  const authFetch = useAuthFetch();

  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [createdPropertyId, setCreatedPropertyId] = useState<string | null>(null);

  // Step 0 — agreement
  const [agreementChecked, setAgreementChecked] = useState(false);
  const [agreementTypedName, setAgreementTypedName] = useState('');
  const [agreementSignerPosition, setAgreementSignerPosition] = useState('');

  // Step 2 — profile (shown after property step)
  const [profile, setProfile] = useState({
    companyName: '',
    contactName: '',
    phone: '',
    portfolioSize: '',
    position: '',
  });

  // Step 2 — property
  const [property, setProperty] = useState({
    label: '',
    address1: '',
    city: '',
    state: '',
    zip: '',
    propertyType: 'RESIDENTIAL',
  });

  // Step 3 — project
  const [project, setProject] = useState({
    projectType: 'ROOF',
    extraInfo: '',
    priority: 'NORMAL' as 'EMERGENCY' | 'HIGH' | 'NORMAL',
    requestedResponseBy: '',
  });

  const toDashboard = useCallback(() => {
    onNavigate('pm-dashboard');
  }, [onNavigate]);

  // ─── Step 0 submit ───────────────────────────────────────────────
  const handleAgreementSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!agreementTypedName.trim()) { setError('Please type your full legal name to sign.'); return; }
    if (!agreementSignerPosition.trim()) { setError('Please enter your position / title.'); return; }
    if (!agreementChecked) { setError('You must agree to the Platform Agreement to continue.'); return; }
    setError(null);
    setSaving(true);
    try {
      await authFetch(getApiUrl('/api/compliance/accept-pm-agreement'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          version: PM_AGREEMENT_VERSION,
          typedName: agreementTypedName.trim(),
          signerPosition: agreementSignerPosition.trim(),
        }),
      });
      // Non-fatal if endpoint returns error — allow progression (agreement will be re-prompted later)
      setStep(1);
    } catch {
      setStep(1); // Non-fatal
    } finally {
      setSaving(false);
    }
  };

  // ─── Step 1 advance (property — validate locally, no API call yet) ───
  const handlePropertyAdvance = (e: React.FormEvent) => {
    e.preventDefault();
    if (!property.address1.trim()) { setError('Address is required.'); return; }
    if (!property.city.trim()) { setError('City is required.'); return; }
    if (!property.state.trim()) { setError('State is required.'); return; }
    if (!property.zip.trim()) { setError('ZIP code is required.'); return; }
    setError(null);
    setStep(2);
  };

  // ─── Step 2 submit (profile + stored property) ───────────────────
  const handleProfileSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!profile.companyName.trim()) { setError('Company name is required.'); return; }
    if (!profile.contactName.trim()) { setError('Your name is required.'); return; }
    if (!profile.phone.trim()) { setError('Phone number is required.'); return; }
    if (!profile.position.trim()) { setError('Position / title is required.'); return; }
    setError(null);
    setSaving(true);
    try {
      const resp = await authFetch(getApiUrl('/api/property-manager/onboard'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          companyName: profile.companyName.trim(),
          contactName: profile.contactName.trim(),
          phone: profile.phone.trim(),
          portfolioSize: profile.portfolioSize || undefined,
          position: profile.position.trim() || undefined,
        }),
      });
      if (!resp.ok) {
        const body = await resp.json().catch(() => ({}));
        throw new Error(body?.error || 'Could not save profile.');
      }
      // Set localStorage so App.tsx role gate allows pm-dashboard access
      try { localStorage.setItem('cb.usertype', 'property_manager'); } catch {}

      // If the user filled in the property step, submit it now (requires PM account to exist)
      if (property.address1.trim()) {
        const propResp = await authFetch(getApiUrl('/api/property-manager/properties'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            label: property.label.trim() || undefined,
            address1: property.address1.trim(),
            city: property.city.trim(),
            state: property.state.trim().toUpperCase(),
            zip: property.zip.trim(),
            propertyType: property.propertyType,
          }),
        });
        if (propResp.ok) {
          const data = await propResp.json();
          setCreatedPropertyId(data?.property?.id || data?.id || null);
        }
      }

      setStep(3);
    } catch (err: any) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // ─── Step 3 submit ───────────────────────────────────────────────
  const handleProjectSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!createdPropertyId) { setStep(4); return; }
    setError(null);
    setSaving(true);
    try {
      const body: Record<string, any> = {
        propertyId: createdPropertyId,
        projectType: project.projectType,
        priority: project.priority,
      };
      if (project.extraInfo.trim()) body.extraInfo = project.extraInfo.trim();
      if (project.requestedResponseBy) body.requestedResponseBy = project.requestedResponseBy;

      const resp = await authFetch(getApiUrl('/api/property-manager/projects'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err?.error || 'Could not create project.');
      }
      setStep(4);
    } catch (err: any) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const goBackStep = () => {
    setError(null);
    setStep((s) => s - 1);
  };

  // ─────────────────────────────────────────────────────────────────
  //  Step renderers
  // ─────────────────────────────────────────────────────────────────

  const renderStep0 = () => (
    <PMOnboardingCard isMobileLayout={isMobileLayout}>
      <PMOnboardingProgressBar step={step} brandGreen={BRAND_GREEN} />
      <PMSectionTitle
        title="Platform Agreement"
        subtitle="Please read and sign the Property Manager Platform Agreement before continuing."
        isMobileLayout={isMobileLayout}
      />
      <PMErrorBanner error={error} />
      <form onSubmit={handleAgreementSubmit}>
        <div style={fieldWrap}>
          <div
            style={{
              maxHeight: 280,
              overflow: 'auto',
              border: '1.5px solid #e2e8f0',
              borderRadius: 10,
              padding: '16px 18px',
              fontSize: 13,
              color: '#475569',
              lineHeight: 1.7,
              background: '#f8fafc',
              whiteSpace: 'pre-wrap',
            }}
          >{`COREBIDS PROPERTY MANAGER PLATFORM AGREEMENT
Version 1 | Effective Date: April 4, 2026

DEFINITIONS

"CoreBids" means CoreBids Inc., a Delaware C corporation, an online project marketplace and payment facilitator. "Platform" means the CoreBids web application, APIs, and related services. "Property Manager" or "PM" means the individual or business entity registering to submit and manage exterior repair and maintenance projects on behalf of properties they manage. "Property" means each physical asset submitted by the PM for project coordination. "Project" means a specific scope of exterior repair, replacement, or maintenance work submitted for contractor bidding. "Contractor" means an independent licensed contractor who receives bid requests through the Platform.

1. RELATIONSHIP OF PARTIES

PM is an independent business operator, not an employee, agent, partner, or joint venturer of CoreBids. By submitting a Property or Project, PM represents and warrants that: (a) PM has full legal authority to request, coordinate, and authorize exterior repair work on each submitted Property; (b) PM has obtained any required owner approvals, HOA authorizations, or lease provisions permitting the requested work; and (c) PM is not submitting work requests for properties where PM has no management authority.

2. PLATFORM PURPOSE AND SCOPE

The CoreBids Platform enables Property Managers to post exterior repair and maintenance projects and receive competitive bids from licensed contractors. CoreBids facilitates connection and payment processing. CoreBids does not guarantee work quality, workmanship, or code compliance; supervise construction; verify contractor licensing or insurance; or act as a general contractor. All warranty and performance obligations run directly between PM and Contractor under the project agreement.

3. USER AUTHORITY AND PROJECT SUBMISSION

Each time PM submits a Project, PM attests that PM has authority to commission and fund the requested work at that Property. PM agrees to provide accurate and complete information for each Property and Project, including correct address, accurate description of work, realistic deadlines (advisory only — not binding on Contractors), and relevant access and site information. Submitting materially false or misleading project information is a material breach.

CoreBids does not guarantee that any Project will receive bids, that any bid will meet PM's budget expectations, or that any Contractor will complete work by any requested deadline.

4. ACCURACY OF PROPERTY AND PROJECT INFORMATION

PM is responsible for the accuracy of all property data, project descriptions, photos, measurements, and reports submitted to the Platform. If measurement reports or third-party data are attached, such data is provided for contractor reference only — Contractors are responsible for their own field verification. CoreBids does not guarantee the accuracy of any measurements or reports.

5. DOCUMENT SHARING AND PERMISSIONS

PM may upload property-level documents (inspection reports, prior work orders, insurance claims, site photos, access instructions) to the Platform. By uploading documents, PM grants CoreBids a limited, non-exclusive license to store and display such documents to Contractors bidding on associated Projects. PM may not upload documents containing sensitive personal information unrelated to the project scope or documents subject to confidentiality restrictions or third-party intellectual property rights.

6. PLATFORM COMMUNICATIONS

PM agrees to receive operational communications from CoreBids including bid notifications, project status updates, milestone and payment confirmations, contractor messages, and platform notices by email to the address associated with PM's account. PM is responsible for keeping the account email current.

7. ACCEPTABLE USE AND ANTI-CIRCUMVENTION

PM may use the Platform only for lawful project coordination for properties PM legitimately manages. PM may not: submit false, duplicate, or placeholder projects; submit projects for properties PM has no authority to manage; use the Platform to solicit contractor contact information for use outside the Platform; or attempt to bypass the CoreBids payment system for any project originating on the Platform. Violation of the anti-circumvention provision is a material breach subject to account suspension and potential liability.

8. LIMITATION OF COREBIDS' ROLE

CoreBids makes no representations that the Platform will be error-free or uninterrupted. CoreBids is not a licensed contractor. PM's recourse for workmanship issues runs directly against the Contractor. Payments are processed by Stripe; CoreBids is not a bank or money transmitter and is not liable for payment processor actions, holds, or account restrictions.

9. DISPUTES AND LIABILITY

In disputes between PM and a Contractor, CoreBids may provide platform-level mediation assistance. CoreBids' determination regarding escrow funds is binding within the platform-defined scope. TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, COREBIDS' TOTAL LIABILITY SHALL NOT EXCEED FEES PAID TO COREBIDS IN THE THREE MONTHS PRECEDING THE CLAIM. COREBIDS SHALL NOT BE LIABLE FOR INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES. PM agrees to indemnify CoreBids against claims arising out of PM's breach of this Agreement, misrepresentation of authority, or violation of applicable law.

10. GOVERNING LAW

This Agreement is governed by the laws of the State of Tennessee. Disputes not resolved informally shall be submitted to binding arbitration under AAA Commercial Rules in Nashville, Tennessee.

11. UPDATES

CoreBids may update this Agreement with reasonable notice by email or in-platform notification. Continued use after the effective date of any update constitutes acceptance. This Agreement, together with the CoreBids Terms of Service and Privacy Policy, constitutes the complete agreement between PM and CoreBids.

CoreBids Inc. · Nashville, TN · support@corebids.com`}</div>
        </div>
        <div style={fieldWrap}>
          <label style={labelStyle}>Full legal name <span style={{ color: '#dc2626' }}>*</span></label>
          <input
            style={inputStyle}
            placeholder="e.g. Jordan Martinez"
            value={agreementTypedName}
            onChange={e => setAgreementTypedName(e.target.value)}
            autoFocus
          />
        </div>
        <div style={fieldWrap}>
          <label style={labelStyle}>Position / Title <span style={{ color: '#dc2626' }}>*</span></label>
          <input
            style={inputStyle}
            placeholder="e.g. CEO, Operations Manager, Property Manager"
            value={agreementSignerPosition}
            onChange={e => setAgreementSignerPosition(e.target.value)}
          />
        </div>
        <div style={{ ...fieldWrap, display: 'flex', alignItems: 'flex-start', gap: 10 }}>
          <input
            id="pm-agreement-checkbox"
            type="checkbox"
            checked={agreementChecked}
            onChange={e => setAgreementChecked(e.target.checked)}
            style={{ marginTop: 2, flexShrink: 0, cursor: 'pointer', width: 16, height: 16 }}
          />
          <label htmlFor="pm-agreement-checkbox" style={{ ...labelStyle, marginBottom: 0, cursor: 'pointer', fontWeight: 400, fontSize: 14, color: '#1f2937' }}>
            I have read and agree to the Property Manager Platform Agreement <span style={{ color: '#dc2626' }}>*</span>
          </label>
        </div>
        <PMPrimaryBtn label="Continue" loading={saving} brandGreen={BRAND_GREEN} />
      </form>
    </PMOnboardingCard>
  );

  // Step 1 — property form (collect locally; submitted after profile in handleProfileSubmit)
  const renderStep1 = () => (
    <PMOnboardingCard isMobileLayout={isMobileLayout}>
      <PMBackBtn show={step > 0 && step < 4} onBack={goBackStep} />
      <PMOnboardingProgressBar step={step} brandGreen={BRAND_GREEN} />
      <PMSectionTitle
        title="Add your first property"
        subtitle="Start with one — you can add more properties from your dashboard at any time."
        isMobileLayout={isMobileLayout}
      />
      <PMErrorBanner error={error} />
      <form onSubmit={handlePropertyAdvance} autoComplete="on">
        <div style={fieldWrap}>
          <label style={labelStyle}>Property Name <span style={{ color: '#94a3b8', fontWeight: 400 }}>(optional — helps you identify it)</span></label>
          <input
            style={inputStyle}
            placeholder="e.g. Belmont Townhomes — Bldg C"
            value={property.label}
            onChange={e => setProperty(p => ({ ...p, label: e.target.value }))}
            autoFocus
          />
        </div>
        <div style={fieldWrap}>
          <label style={labelStyle}>Street Address <span style={{ color: '#dc2626' }}>*</span></label>
          <input
            style={inputStyle}
            placeholder="e.g. 1420 Belmont Blvd"
            value={property.address1}
            onChange={e => setProperty(p => ({ ...p, address1: e.target.value }))}
          />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 12, marginBottom: 18 }}>
          <div>
            <label style={labelStyle}>City <span style={{ color: '#dc2626' }}>*</span></label>
            <input style={inputStyle} placeholder="Nashville" value={property.city} onChange={e => setProperty(p => ({ ...p, city: e.target.value }))} />
          </div>
          <div>
            <label style={labelStyle}>State <span style={{ color: '#dc2626' }}>*</span></label>
            <input style={inputStyle} placeholder="TN" maxLength={2} value={property.state} onChange={e => setProperty(p => ({ ...p, state: e.target.value }))} />
          </div>
          <div>
            <label style={labelStyle}>ZIP <span style={{ color: '#dc2626' }}>*</span></label>
            <input style={inputStyle} placeholder="37212" value={property.zip} onChange={e => setProperty(p => ({ ...p, zip: e.target.value }))} />
          </div>
        </div>
        <div style={fieldWrap}>
          <label style={labelStyle}>Property Type</label>
          <select style={{ ...inputStyle, appearance: 'auto' }} value={property.propertyType} onChange={e => setProperty(p => ({ ...p, propertyType: e.target.value }))}>
            {PROPERTY_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </div>
        <PMPrimaryBtn label="Save and Continue" loading={saving} brandGreen={BRAND_GREEN} />
        <PMSkipLink
          label="Skip for now — I'll add properties from the dashboard"
          onClick={() => {
            setError(null);
            setStep(2);
          }}
        />
      </form>
    </PMOnboardingCard>
  );

  // Step 2 — profile form (also submits stored property data via handleProfileSubmit)
  const renderStep2 = () => (
    <PMOnboardingCard isMobileLayout={isMobileLayout}>
      <PMBackBtn show={step > 0 && step < 4} onBack={goBackStep} />
      <PMOnboardingProgressBar step={step} brandGreen={BRAND_GREEN} />
      <PMSectionTitle
        title="Let's get you set up"
        subtitle="This will only take a minute. You can update any of this later."
        isMobileLayout={isMobileLayout}
      />
      <PMErrorBanner error={error} />
      <form onSubmit={handleProfileSubmit} autoComplete="on">
        <div style={fieldWrap}>
          <label style={labelStyle}>Company Name <span style={{ color: '#dc2626' }}>*</span></label>
          <input
            style={inputStyle}
            placeholder="e.g. Pinnacle Property Management"
            value={profile.companyName}
            onChange={e => setProfile(p => ({ ...p, companyName: e.target.value }))}
          />
        </div>
        <div style={fieldWrap}>
          <label style={labelStyle}>Your Name <span style={{ color: '#dc2626' }}>*</span></label>
          <input
            style={inputStyle}
            placeholder="e.g. Jordan Martinez"
            value={profile.contactName}
            onChange={e => setProfile(p => ({ ...p, contactName: e.target.value }))}
          />
        </div>
        <div style={fieldWrap}>
          <label style={labelStyle}>Position / Title <span style={{ color: '#dc2626' }}>*</span></label>
          <input
            style={inputStyle}
            placeholder="e.g. Property Manager, Facilities Director"
            value={profile.position}
            onChange={e => setProfile(p => ({ ...p, position: e.target.value }))}
          />
        </div>
        <div style={fieldWrap}>
          <label style={labelStyle}>Phone <span style={{ color: '#dc2626' }}>*</span></label>
          <input
            style={inputStyle}
            type="tel"
            placeholder="e.g. 615-555-0100"
            value={profile.phone}
            onChange={e => setProfile(p => ({ ...p, phone: e.target.value }))}
          />
        </div>
        <div style={fieldWrap}>
          <label style={labelStyle}>Number of Properties <span style={{ color: '#94a3b8', fontWeight: 400 }}>(roughly)</span></label>
          <select
            style={{ ...inputStyle, appearance: 'auto' }}
            value={profile.portfolioSize}
            onChange={e => setProfile(p => ({ ...p, portfolioSize: e.target.value }))}
          >
            <option value="">Select…</option>
            {PORTFOLIO_SIZES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
        </div>
        <PMPrimaryBtn label="Continue" loading={saving} brandGreen={BRAND_GREEN} />
        <PMReassurance text="No credit card required. Account is free." />
      </form>
    </PMOnboardingCard>
  );

  const renderStep3 = () => (
    <PMOnboardingCard isMobileLayout={isMobileLayout}>
      <PMBackBtn show={step > 0 && step < 4} onBack={goBackStep} />
      <PMOnboardingProgressBar step={step} brandGreen={BRAND_GREEN} />
      <PMSectionTitle
        title="Create your first project"
        subtitle="Try CoreBids on one repair or replacement job. You'll receive contractor bids after submitting."
        isMobileLayout={isMobileLayout}
      />
      {!createdPropertyId && (
        <div style={{ background: '#fef9c3', border: '1px solid #fde68a', borderRadius: 10, padding: '12px 16px', marginBottom: 20, fontSize: 14, color: '#854d0e' }}>
          You skipped adding a property. You can still submit a project — it won&apos;t be linked to a specific property address yet.
        </div>
      )}
      <PMErrorBanner error={error} />
      <form onSubmit={handleProjectSubmit}>
        <div style={fieldWrap}>
          <label style={labelStyle}>Type of Work <span style={{ color: '#dc2626' }}>*</span></label>
          <select style={{ ...inputStyle, appearance: 'auto' }} value={project.projectType} onChange={e => setProject(p => ({ ...p, projectType: e.target.value }))}>
            {PROJECT_TYPES.map(t => <option key={t} value={t}>{PROJECT_TYPE_LABELS[t]}</option>)}
          </select>
        </div>
        <div style={fieldWrap}>
          <label style={labelStyle}>Brief Description <span style={{ color: '#94a3b8', fontWeight: 400 }}>(optional)</span></label>
          <textarea
            style={{ ...inputStyle, resize: 'vertical', minHeight: 80 }}
            placeholder="e.g. Active leak on south face near HVAC unit. Photos available."
            value={project.extraInfo}
            onChange={e => setProject(p => ({ ...p, extraInfo: e.target.value }))}
          />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: isMobileLayout ? '1fr' : '1fr 1fr', gap: 12, marginBottom: 18 }}>
          <div>
            <label style={labelStyle}>Priority</label>
            <select style={{ ...inputStyle, appearance: 'auto' }} value={project.priority} onChange={e => setProject(p => ({ ...p, priority: e.target.value as any }))}>
              <option value="NORMAL">Normal</option>
              <option value="HIGH">High</option>
              <option value="EMERGENCY">Emergency</option>
            </select>
          </div>
          <div>
            <label style={labelStyle}>Respond By <span style={{ color: '#94a3b8', fontWeight: 400 }}>(optional)</span></label>
            <input
              style={inputStyle}
              type="date"
              value={project.requestedResponseBy}
              onChange={e => setProject(p => ({ ...p, requestedResponseBy: e.target.value }))}
              min={new Date().toISOString().split('T')[0]}
            />
          </div>
        </div>
        <PMPrimaryBtn label="Create Project" loading={saving} brandGreen={BRAND_GREEN} />
        {/* CB:BEGIN pm-skip-step4of5 — First project (Step 4 of 5): optional skip to success */}
        <PMSkipLink
          label="Skip — I'll create projects from the dashboard"
          onClick={() => {
            setError(null);
            setStep(4);
          }}
        />
        {/* CB:END pm-skip-step4of5 */}
        <PMReassurance text="You can add more details and attachments from the dashboard after submitting." />
      </form>
    </PMOnboardingCard>
  );

  const renderStep4 = () => (
    <PMOnboardingCard isMobileLayout={isMobileLayout}>
      <div style={{ textAlign: 'center', padding: '8px 0 24px' }}>
        <div
          style={{
            display: 'inline-flex',
            padding: 20,
            background: '#e6f5ef',
            borderRadius: '50%',
            marginBottom: 24,
          }}
        >
          <CheckCircle2 size={48} style={{ color: BRAND_GREEN }} />
        </div>
        <h2 style={{ fontSize: isMobileLayout ? 24 : 28, fontWeight: 800, color: '#0f172a', marginBottom: 12 }}>
          You're all set.
        </h2>
        <p style={{ fontSize: 16, color: '#64748b', lineHeight: 1.6, maxWidth: 400, margin: '0 auto 32px' }}>
          Your account is active. Head to your dashboard to track bids, manage properties, and add more projects.
        </p>
        <div
          style={{
            background: '#f8fafc',
            border: '1.5px solid #e2e8f0',
            borderRadius: 14,
            padding: '20px 24px',
            marginBottom: 28,
            textAlign: 'left',
          }}
        >
          <div style={{ fontSize: 12, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 12 }}>What happens next</div>
          {[
            'Contractors in your area will see your project in their bid feed.',
            'You will receive bids within 24\u201372 hours.',
            'Compare bids side by side and select the best fit.',
            'Payments are milestone-protected through escrow.',
          ].map(s => (
            <div key={s} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 10 }}>
              <div style={{ width: 7, height: 7, borderRadius: '50%', background: BRAND_GREEN, flexShrink: 0, marginTop: 6 }} />
              <span style={{ fontSize: 14, color: '#475569', lineHeight: 1.5 }}>{s}</span>
            </div>
          ))}
        </div>
        <button
          onClick={toDashboard}
          style={{
            width: '100%',
            padding: '16px 24px',
            background: BRAND_GREEN,
            color: '#fff',
            border: 'none',
            borderRadius: 12,
            fontSize: 16,
            fontWeight: 700,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 8,
            minHeight: 48,
          }}
        >
          Go to My Dashboard <ArrowRight size={18} />
        </button>
      </div>
    </PMOnboardingCard>
  );

  return (
    <div style={{ minHeight: '100vh', background: '#f8fafc', fontFamily: 'Inter, system-ui, -apple-system, sans-serif' }}>
      {/* Header */}
      <header style={{ background: '#fff', borderBottom: '1px solid #e2e8f0', padding: '0 24px' }}>
        <ContentLimiter>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: 60 }}>
            <button
              onClick={() => onNavigate('pm-landing')}
              style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, fontWeight: 800, color: BRAND_GREEN, letterSpacing: '-0.02em' }}
            >
              CoreBids
            </button>
            {step < 4 && (
              <span style={{ fontSize: 13, color: '#94a3b8' }}>
                Step {step + 1} of {TOTAL_STEPS}
              </span>
            )}
          </div>
        </ContentLimiter>
      </header>

      {/* Hero accent */}
      <div style={{ height: 6, background: `linear-gradient(90deg, ${BRAND_GREEN} 0%, #1a6b69 50%, #34d399 100%)` }} />

      {/* Body */}
      <section style={{ padding: isMobileLayout ? '32px 16px 64px' : '56px 40px 80px' }}>
        <ContentLimiter>
          {/* Welcome banner — only on first step */}
          {step === 0 && (
            <div style={{ textAlign: 'center', marginBottom: 32 }}>
              <div
                style={{
                  display: 'inline-block',
                  background: 'linear-gradient(135deg, #123c3b 0%, #1a6b69 100%)',
                  borderRadius: 24,
                  padding: '6px 16px',
                  fontSize: 12,
                  fontWeight: 700,
                  color: '#a7f3d0',
                  marginBottom: 16,
                  textTransform: 'uppercase',
                  letterSpacing: '0.06em',
                }}
              >
                Welcome to CoreBids
              </div>
              <h1 style={{ fontSize: isMobileLayout ? 26 : 32, fontWeight: 900, color: '#0f172a', marginBottom: 8 }}>
                Set up your property manager account
              </h1>
              <p style={{ fontSize: 15, color: '#64748b', maxWidth: 480, margin: '0 auto' }}>
                Start with one property. No process change required. You can update everything later.
              </p>
            </div>
          )}

          {step === 0 && renderStep0()}
          {step === 1 && renderStep1()}
          {step === 2 && renderStep2()}
          {step === 3 && renderStep3()}
          {step === 4 && renderStep4()}
        </ContentLimiter>
      </section>

      {/* Footer note */}
      <div style={{ textAlign: 'center', padding: '0 16px 32px', fontSize: 13, color: '#94a3b8' }}>
        <Building2 size={14} style={{ display: 'inline', marginRight: 6, verticalAlign: 'middle' }} />
        CoreBids Property Manager — no commitment, no exclusivity. Start with one property.
      </div>
    </div>
  );
};

export default PropertyManagerOnboarding;
// CB:END pm-channel-onboarding
